import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {UpdateWorkshop, Workshop} from '../types/Workshop';
import workshopsService from '../services/workshopsService';
import {handleError} from '../../../common/utils/errorHandling';
import {useNavigate, useParams} from 'react-router-dom';
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton, AdminForm, AdminFormSection, AdminInputDropdown, AdminInputText} from "../../../layout/admin";
import WorkshopMembers from './WorkshopMembers';
import {useFormErrorHandling} from "../../../common/hooks/useFormErrorHandling";

const WorkshopForm: React.FC = () => {
  const {t} = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    parentId: undefined as string | undefined,
    memberIds: [] as string[]
  });
  const [workshop, setWorkshop] = useState<Workshop | null>(null);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const {id} = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const {errors, handleApiError, clearErrors} = useFormErrorHandling();

  useEffect(() => {
    fetchWorkshops();
    if (isEditMode && id) {
      fetchWorkshop(id);
    }
  }, [id, isEditMode]);

  const fetchWorkshops = async (): Promise<void> => {
    try {
      const workshopsData = await workshopsService.getAllWorkshops();
      setWorkshops(workshopsData);
    } catch (error) {
      console.error('Error fetching workshops:', error);
    }
  };

  const fetchWorkshop = async (workshopId: string): Promise<void> => {
    setLoading(true);
    try {
      const workshopData = await workshopsService.getWorkshop(workshopId);
      setWorkshop(workshopData);
      setFormData({
        name: workshopData.name,
        location: workshopData.location || '',
        parentId: workshopData.parent?.id,
        memberIds: workshopData.memberIds || []
      });
    } catch (error) {
      console.error('Error fetching workshop:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string): void => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMemberIdsChange = (memberIds: string[]): void => {
    setFormData(prev => ({
      ...prev,
      memberIds
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    await handleError(
      async () => {
        if (isEditMode && workshop) {
          const updatedWorkshop: UpdateWorkshop = {
            id: workshop.id,
            name: formData.name,
            location: formData.location,
            parentId: formData.parentId,
            memberIds: formData.memberIds
          };
          await workshopsService.updateWorkshop(updatedWorkshop);
        } else {
          await workshopsService.createWorkshop({
            name: formData.name,
            location: formData.location,
            parentId: formData.parentId,
            memberIds: formData.memberIds
          });
        }
        navigate('/workshops');
      },
      isEditMode ? t('admin.workshops.errors.saving') : t('admin.workshops.errors.saving')
    );

    setLoading(false);
  };

  if (loading && isEditMode) {
    return <div>{t('admin.common.loading')}</div>;
  }

  return (
    <>
      <AdminFormHeader
        title={isEditMode ? t('admin.workshops.title.edit') : t('admin.workshops.title.create')}
        subtitle={isEditMode ? t('admin.workshops.subtitle.edit') : t('admin.workshops.subtitle.create')}
      />

      <AdminForm
        leftColumn={
          <>
            <AdminFormSection title={t('admin.common.sections.basicInfo')}>
              <AdminInputText
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                name="name"
                id="name"
                label={t('admin.common.fields.name')}
                required={true}
                error={errors.name}
              />

              <AdminInputText
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                name="location"
                id="location"
                label={t('admin.workshops.fields.location')}
                error={errors.location}
              />

              <AdminInputDropdown
                value={formData.parentId || ''}
                onChange={(e) => handleInputChange('parentId', e.target.value)}
                options={workshops
                  .filter(w => w.id !== id)
                  .map(w => ({
                    label: w.location ? `${w.name} (${w.location})` : w.name,
                    value: w.id
                  }))}
                name="parentId"
                id="parentId"
                label={t('admin.workshops.fields.parentWorkshop')}
                placeholder={t('admin.workshops.placeholders.selectParent')}
              />
            </AdminFormSection>
          </>
        }
        rightColumn={
          <>
            <AdminFormSection title={t('admin.workshops.sections.members')}>
              <WorkshopMembers
                memberIds={formData.memberIds}
                onMemberIdsChange={handleMemberIdsChange}
              />
            </AdminFormSection>
          </>
        }
        formActions={
          <>
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={() => navigate("/workshops")}
            />
            <AdminButton
              type="submit"
              label={workshop ? t('admin.common.actions.update') : t('admin.common.actions.create')}
              variant="black"
              disabled={loading}
            />
          </>
        }
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default WorkshopForm;

